import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Company Registration Notice Page
 *
 * SOC 2 Control: CC6.1 - Tenant Provisioning Control
 *
 * Self-service registration has been disabled to comply with SOC 2 requirements.
 * Tenant provisioning is now admin-only through the Platform Admin Portal.
 */
const CompanyRegister = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4" data-testid="company-register-page">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <img
              src="/ergoniq-logo.svg"
              alt="Ergoniq Logo"
              className="h-20 w-auto"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div className="hidden flex-col items-center">
              <div className="text-4xl font-bold bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] bg-clip-text text-transparent">
                saarv
              </div>
              <div className="text-xs text-[#1B5C94] font-semibold tracking-widest">RISE WITH KNOWLEDGE</div>
            </div>
          </div>

          {/* Info Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
              <svg
                className="w-10 h-10 text-[#1B5C94]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-gray-800 mb-4">Self-Service Registration Unavailable</h2>
          <p className="text-lg text-gray-600 mb-6">
            Tenant registration has been moved to a controlled provisioning process to maintain SOC 2 compliance.
          </p>
        </div>

        <div className="space-y-6">
          {/* Notice Card */}
          <div className="bg-blue-50 border-l-4 border-[#1B5C94] p-6 rounded-r-lg">
            <h3 className="text-lg font-bold text-[#1B5C94] mb-3">How to Get Started</h3>
            <p className="text-gray-700 mb-4">
              To create a new tenant account on the SAARV Safety platform, please contact our team:
            </p>

            <div className="space-y-3 mb-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-[#1B5C94] mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="font-semibold text-gray-800">Email Sales Team</p>
                  <a
                    href="mailto:sales@saarv.in"
                    className="text-[#3AC0DB] hover:underline"
                  >
                    sales@saarv.in
                  </a>
                </div>
              </div>

              <div className="flex items-start">
                <svg className="w-5 h-5 text-[#1B5C94] mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div>
                  <p className="font-semibold text-gray-800">View Documentation</p>
                  <a
                    href="https://docs.saarvsafety.com/tenant-provisioning"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#3AC0DB] hover:underline"
                  >
                    Tenant Provisioning Guide
                  </a>
                </div>
              </div>

              <div className="flex items-start">
                <svg className="w-5 h-5 text-[#1B5C94] mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
                <div>
                  <p className="font-semibold text-gray-800">Platform Admin Access</p>
                  <a
                    href="/saarv-admin/login"
                    className="text-[#3AC0DB] hover:underline"
                  >
                    Admin Portal Login
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* What to Expect */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-bold text-gray-800 mb-3">What to Expect</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-[#3AC0DB] mr-2 mt-1">•</span>
                <span>Our team will review your request and create your tenant account</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#3AC0DB] mr-2 mt-1">•</span>
                <span>You'll receive a welcome email with your admin credentials</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#3AC0DB] mr-2 mt-1">•</span>
                <span>Initial modules will be configured based on your subscription plan</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#3AC0DB] mr-2 mt-1">•</span>
                <span>Your first login will require you to set a new secure password</span>
              </li>
            </ul>
          </div>

          {/* Already have an account? */}
          <div className="text-center pt-4 border-t border-gray-200">
            <p className="text-gray-700 mb-3">Already have an account?</p>
            <button
              onClick={() => navigate('/login')}
              className="bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] text-white font-bold py-3 px-8 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
            >
              Login to Your Account
            </button>
          </div>

          {/* SOC 2 Compliance Notice */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-green-800 mb-1">SOC 2 Type II Compliant</p>
                <p className="text-xs text-green-700">
                  This controlled provisioning process ensures compliance with SOC 2 security standards (CC6.1 - Tenant Provisioning Control).
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyRegister;
